import { useState, useEffect } from 'react';

import {
  getProposals, createProposal, updateProposal, deleteProposal,
  getClients, getCRMLeads, getUserDirectory,
} from '../services/api';
import { useAuth } from '../context/AuthContext';
import { showSuccessToast, showErrorToast } from '../utils/toastConfig';
import { FiPlus, FiEdit2, FiTrash2, FiRefreshCw } from 'react-icons/fi';
import './crmPages.css';

const STATUS_OPTIONS = ['Draft', 'Sent', 'Viewed', 'Accepted', 'Rejected', 'Expired'];
const formatDate  = (d) => (d ? new Date(d).toLocaleDateString('en-IN') : '—');
const formatMoney = (n) => '₹' + Number(n || 0).toLocaleString('en-IN');
const badgeClass  = (s) => 'cp-badge cp-badge--' + String(s || 'draft').toLowerCase();

const emptyForm = {
  title: '',
  lead_id: '',
  client_id: '',
  amount: '',
  discount: 0,
  tax_percent: 18,
  valid_until: '',
  status: 'Draft',
  terms: '',
  notes: '',
  owner_id: '',
};

export default function Proposals() {
  const { user } = useAuth();
  const isCEO = user?.role === 'ceo' || user?.role === 'admin';
  const isHR  = user?.role === 'hr';

  const [proposals, setProposals] = useState([]);
  const [leads, setLeads] = useState([]);
  const [clients, setClients] = useState([]);
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(emptyForm);
  const [saving, setSaving] = useState(false);

  const loadAll = async () => {
    setLoading(true);
    try {
      // Sales users don't need the directory (they can't assign owners)
      const isSalesUser = user?.role === 'sales';
      const calls = [
        getProposals(),
        getCRMLeads(),
        getClients(),
        isSalesUser ? Promise.resolve({ data: [] }) : getUserDirectory(),
      ];
      const [p, l, c, u] = await Promise.allSettled(calls);
      setProposals(p.status === 'fulfilled' ? (p.value.data || []) : []);
      setLeads(l.status === 'fulfilled' ? (l.value.data || []) : []);
      setClients(c.status === 'fulfilled' ? (c.value.data || []) : []);
      setUsers((u.status === 'fulfilled' ? (u.value.data || []) : []).filter((x) => x.role === 'sales'));
      if (p.status === 'rejected') {
        showErrorToast('Failed to load proposals');
      }
    } catch (err) {
      showErrorToast('Failed to load proposals');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { loadAll(); }, []);

  const openCreate = () => {
    setEditing(null);
    setForm(emptyForm);
    setShowModal(true);
  };

  const openEdit = (p) => {
    setEditing(p);
    setForm({
      title: p.title || '',
      lead_id: p.lead_id || '',
      client_id: p.client_id || '',
      amount: p.amount || '',
      discount: p.discount || 0,
      tax_percent: p.tax_percent != null ? p.tax_percent : 18,
      valid_until: p.valid_until ? p.valid_until.slice(0, 10) : '',
      status: p.status || 'Draft',
      terms: p.terms || '',
      notes: p.notes || '',
      owner_id: p.owner_id || '',
    });
    setShowModal(true);
  };

  const handleChange = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const computedTotal = (() => {
    const subtotal = Math.max(0, Number(form.amount || 0) - Number(form.discount || 0));
    const tax = subtotal * (Number(form.tax_percent || 0) / 100);
    return subtotal + tax;
  })();

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.title.trim()) { showErrorToast('Title is required'); return; }
    setSaving(true);
    try {
      const payload = {
        ...form,
        lead_id: form.lead_id || null,
        client_id: form.client_id || null,
        owner_id: form.owner_id || null,
      };
      if (editing) {
        await updateProposal(editing.id, payload);
        showSuccessToast('Proposal updated');
      } else {
        await createProposal(payload);
        showSuccessToast('Proposal created');
      }
      setShowModal(false);
      await loadAll();
    } catch (err) {
      showErrorToast(err.response?.data?.message || 'Save failed');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (p) => {
    if (!window.confirm(`Delete proposal ${p.proposal_number}?`)) return;
    try {
      await deleteProposal(p.id);
      showSuccessToast('Proposal deleted');
      await loadAll();
    } catch (err) {
      showErrorToast(err.response?.data?.message || 'Delete failed');
    }
  };

  // Stats
  const drafts   = proposals.filter((p) => p.status === 'Draft').length;
  const sent     = proposals.filter((p) => p.status === 'Sent' || p.status === 'Viewed').length;
  const accepted = proposals.filter((p) => p.status === 'Accepted').length;
  const totalValue = proposals
    .filter((p) => p.status === 'Accepted')
    .reduce((s, p) => s + Number(p.total_amount || 0), 0);

  return (
    <div className="cp-shell">

      <main className="cp-main">
        <div className="cp-page-header">
          <div>
            <h1>Proposals</h1>
            <p>Draft, send, and track proposals. Accepted proposals feed directly into contracts and invoices.</p>
          </div>
          <div style={{ display: 'flex', gap: 8 }}>
            <button className="cp-btn cp-btn--ghost" onClick={loadAll}><FiRefreshCw /> Refresh</button>
            <button className="cp-btn cp-btn--primary" onClick={openCreate}><FiPlus /> New Proposal</button>
          </div>
        </div>

        <div className="cp-stats">
          <div className="cp-stat"><div className="cp-stat__label">Total Proposals</div><div className="cp-stat__value">{proposals.length}</div></div>
          <div className="cp-stat"><div className="cp-stat__label">Drafts</div><div className="cp-stat__value">{drafts}</div></div>
          <div className="cp-stat"><div className="cp-stat__label">Sent / Viewed</div><div className="cp-stat__value">{sent}</div></div>
          <div className="cp-stat"><div className="cp-stat__label">Accepted Value</div><div className="cp-stat__value">{formatMoney(totalValue)}</div><div className="cp-stat__sub">{accepted} accepted</div></div>
        </div>

        {loading ? (
          <div className="cp-empty">Loading proposals...</div>
        ) : proposals.length === 0 ? (
          <div className="cp-card cp-empty">
            <div className="cp-empty__title">No proposals yet</div>
            <div>Click "New Proposal" to draft your first one.</div>
          </div>
        ) : (
          <div className="cp-table-wrap">
            <table className="cp-table">
              <thead>
                <tr>
                  <th>Number</th>
                  <th>Title</th>
                  <th>Lead / Client</th>
                  <th>Amount</th>
                  <th>Total</th>
                  <th>Valid Until</th>
                  <th>Status</th>
                  {(isCEO || isHR) && <th>Owner</th>}
                  <th style={{ width: 110 }}>Actions</th>
                </tr>
              </thead>
              <tbody>
                {proposals.map((p) => (
                  <tr key={p.id}>
                    <td style={{ fontWeight: 600 }}>{p.proposal_number}</td>
                    <td>{p.title}</td>
                    <td>{p.client_name || p.lead_name || '—'}</td>
                    <td>{formatMoney(p.amount)}</td>
                    <td>{formatMoney(p.total_amount)}</td>
                    <td>{formatDate(p.valid_until)}</td>
                    <td><span className={badgeClass(p.status)}>{p.status}</span></td>
                    {(isCEO || isHR) && <td>{p.owner_full_name || p.owner_name || '—'}</td>}
                    <td>
                      <button className="cp-btn cp-btn--ghost cp-btn--sm" onClick={() => openEdit(p)} title="Edit"><FiEdit2 /></button>
                      {(isCEO || isHR) && (
                        <button className="cp-btn cp-btn--danger cp-btn--sm" onClick={() => handleDelete(p)} title="Delete" style={{ marginLeft: 4 }}>
                          <FiTrash2 />
                        </button>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        {showModal && (
          <div className="cp-modal-backdrop" onClick={() => !saving && setShowModal(false)}>
            <div className="cp-modal" onClick={(e) => e.stopPropagation()}>
              <div className="cp-modal__header">
                <h2>{editing ? `Edit ${editing.proposal_number}` : 'New Proposal'}</h2>
                <button className="cp-modal__close" onClick={() => setShowModal(false)}>×</button>
              </div>
              <form className="cp-form" onSubmit={handleSubmit}>
                <div className="cp-field cp-field--full">
                  <label>Title *</label>
                  <input value={form.title} onChange={(e) => handleChange('title', e.target.value)} required />
                </div>
                <div className="cp-field">
                  <label>Lead</label>
                  <select value={form.lead_id} onChange={(e) => handleChange('lead_id', e.target.value)}>
                    <option value="">— None —</option>
                    {leads.map((l) => <option key={l.id} value={l.id}>{l.name}{l.company ? ` · ${l.company}` : ''}</option>)}
                  </select>
                </div>
                <div className="cp-field">
                  <label>Client</label>
                  <select value={form.client_id} onChange={(e) => handleChange('client_id', e.target.value)}>
                    <option value="">— None —</option>
                    {clients.map((c) => <option key={c.id} value={c.id}>{c.client_name}</option>)}
                  </select>
                </div>
                <div className="cp-field">
                  <label>Amount (₹) *</label>
                  <input type="number" step="0.01" value={form.amount} onChange={(e) => handleChange('amount', e.target.value)} required />
                </div>
                <div className="cp-field">
                  <label>Discount (₹)</label>
                  <input type="number" step="0.01" value={form.discount} onChange={(e) => handleChange('discount', e.target.value)} />
                </div>
                <div className="cp-field">
                  <label>Tax %</label>
                  <input type="number" step="0.01" value={form.tax_percent} onChange={(e) => handleChange('tax_percent', e.target.value)} />
                </div>
                <div className="cp-field">
                  <label>Computed Total</label>
                  <input disabled value={formatMoney(computedTotal)} />
                </div>
                <div className="cp-field">
                  <label>Valid Until</label>
                  <input type="date" value={form.valid_until} onChange={(e) => handleChange('valid_until', e.target.value)} />
                </div>
                <div className="cp-field">
                  <label>Status</label>
                  <select value={form.status} onChange={(e) => handleChange('status', e.target.value)}>
                    {STATUS_OPTIONS.map((s) => <option key={s} value={s}>{s}</option>)}
                  </select>
                </div>
                {(isCEO || isHR) && (
                  <div className="cp-field cp-field--full">
                    <label>Assigned To</label>
                    <select value={form.owner_id} onChange={(e) => handleChange('owner_id', e.target.value)}>
                      <option value="">— Unassigned —</option>
                      {users.map((u) => <option key={u.id} value={u.id}>{u.full_name || u.username}</option>)}
                    </select>
                  </div>
                )}
                <div className="cp-field cp-field--full">
                  <label>Terms</label>
                  <textarea rows={2} value={form.terms} onChange={(e) => handleChange('terms', e.target.value)} />
                </div>
                <div className="cp-field cp-field--full">
                  <label>Notes</label>
                  <textarea rows={2} value={form.notes} onChange={(e) => handleChange('notes', e.target.value)} />
                </div>
                <div className="cp-form-actions">
                  <button type="button" className="cp-btn cp-btn--ghost" onClick={() => setShowModal(false)}>Cancel</button>
                  <button type="submit" className="cp-btn cp-btn--primary" disabled={saving}>
                    {saving ? 'Saving…' : (editing ? 'Save Changes' : 'Create Proposal')}
                  </button>
                </div>
              </form>
            </div>
          </div>
        )}
      </main>
    </div>
  );
}
